/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { PCView, SPView } from "components/common/MediaQuery";
import { format } from "date-fns";
import { useI18n } from "hooks/i18n/i18n";
import { withNoTranslate } from "i18n/noTranslate";
import React from "react";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { LinkButton } from "ui/Button";
import { DetailsTable } from "ui/DetailsTable";
import { MobileBreadcrumbNavigation } from "ui/MobileBreadcrumbNavigation";
import { Stepper } from "ui/Stepper";
import { SubContentsLayout } from "ui/SubContentsLayout";

import { ResaleApplicationCompletionMessage } from "./ResaleApplicationCompletionMessage";
import { ResaleEventSummary } from "./ResaleEventSummary";

import type { FC } from "react";

type Props = {
  creditCard: {
    brand: string;
    lastFourDigits: string;
  };
  desiredQuantity: number;
  eventId: string;
  eventOpenAt: number;
  eventStartAt: number;
  eventThumbnailUrl: string;
  eventTitle: string;
  lotteryDate: number;
  ticketName: string;
  ticketPriceAmount: number;
};

export const ResaleApplicationCompletion: FC<Props> = ({
  creditCard,
  desiredQuantity,
  eventId,
  eventOpenAt,
  eventStartAt,
  eventThumbnailUrl,
  eventTitle,
  lotteryDate,
  ticketName,
  ticketPriceAmount,
}) => {
  const { t, i18n } = useI18n();
  const classes = styles();
  const pageTitle = t("event.resale.action.apply");
  const eventOpenAtDate = new Date(eventOpenAt);
  const weekdayFormatter = new Intl.DateTimeFormat(
    i18n.language.replace("_", "-"),
    { weekday: "short" }
  );
  const eventDateTime = `${format(
    eventOpenAtDate,
    "yyyy/MM/dd"
  )} (${weekdayFormatter.format(eventOpenAtDate)}) ${format(
    eventOpenAtDate,
    "HH:mm"
  )} / ${format(new Date(eventStartAt), "HH:mm")}`;
  const resaleResultAt = `${format(
    new Date(lotteryDate),
    "yyyy/MM/dd HH:mm"
  )} ${t("event.resale.after")}`;
  const ticketSubtotal = `¥${(
    ticketPriceAmount * desiredQuantity
  ).toLocaleString()}`;

  return (
    <SubContentsLayout>
      <PCView>
        <BreadcrumbNavigation
          breadcrumbs={[
            { label: t("common.routes.home"), link: "/" },
            { label: t("event.resale.entry.eventList"), link: "/events" },
            { label: eventTitle, link: `/events/${eventId}` },
            { label: pageTitle, link: null },
          ]}
        />
      </PCView>
      <SPView>
        <MobileBreadcrumbNavigation
          href={`/events/${eventId}`}
          label={pageTitle}
        />
      </SPView>
      <div css={classes.root}>
        <Stepper
          ariaLabel={t("event.resale.entry.steps.ariaLabel")}
          currentStepId="completion"
          steps={[
            {
              id: "quantitySelection",
              label: t("event.resale.entry.steps.quantity"),
            },
            {
              id: "confirmation",
              label: t("event.resale.entry.steps.confirmation"),
            },
            {
              id: "completion",
              label: t("event.resale.entry.steps.completion"),
            },
          ]}
        />
        <ResaleApplicationCompletionMessage />
        <ResaleEventSummary
          eventDateTime={eventDateTime}
          eventThumbnailURL={eventThumbnailUrl}
          eventTitle={eventTitle}
        />
        <DetailsTable
          rows={[
            {
              label: t("event.resale.entry.completion.applicationDetails"),
              value: (
                <span>
                  <span {...withNoTranslate()}>{ticketName}</span>{" "}
                  {t("event.resale.entry.confirmation.desiredQuantityValue", {
                    quantity: desiredQuantity,
                  })}
                </span>
              ),
            },
            {
              label: t("event.resale.entry.completion.lotteryResult"),
              value: <span {...withNoTranslate()}>{resaleResultAt}</span>,
            },
            {
              label: t("event.resale.entry.completion.paymentAmount"),
              value: <span {...withNoTranslate()}>{ticketSubtotal}</span>,
            },
            {
              label: t("event.resale.entry.completion.paymentMethod"),
              value: (
                <span {...withNoTranslate()}>
                  {creditCard.brand} •••• {creditCard.lastFourDigits}
                </span>
              ),
            },
          ]}
        />
        <div css={classes.actions}>
          <LinkButton href="/account/lottery" size="full" variant="outline">
            {t("event.resale.entry.completion.viewApplicationStatus")}
          </LinkButton>
          <LinkButton
            href={`/events/${eventId}`}
            size="full"
            variant="neutralOutline"
          >
            {t("event.resale.entry.completion.returnToEventPage")}
          </LinkButton>
        </div>
      </div>
    </SubContentsLayout>
  );
};

const styles = () => ({
  root: css`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 92%;
    max-width: 760px;
    padding: 24px 0 0;
    margin: 0 auto;
    @media screen and (min-width: 768px) {
      gap: 32px;
      padding-top: 40px;
    }
  `,
  actions: css`
    display: grid;
    gap: 8px;
  `,
});
